import Link from 'next/link';
import { useTranslations } from 'next-intl';
import React from 'react';

import { useDomainConfiguration } from '@/components/DomainConfigurationProvider';

import { PressQuote } from '@cineamo/api/models/premium-movie-landing-page-configuration/PremiumMovieLandingPageConfigurationDto';

import { IconExternalLink16 } from '@cineamo/ui/src/icons/ic-externalLink';
import { IconStarSolid16 } from '@cineamo/ui/src/icons/ic-starSolid';

import SectionHeader from './SectionHeader';

const RatingStars: React.FC<{ rating: number; quoteIndex: number }> = ({ rating, quoteIndex }) => {
    const { domainConfiguration } = useDomainConfiguration();
    const t = useTranslations();
    const { style } = domainConfiguration;
    const primaryColor = style.primaryColor;

    return (
        <div
            className="flex space-x-2 justify-center lg:justify-start"
            role="img"
            aria-label={t('common.rating-stars', { rating })}>
            {Array.from({ length: rating }).map((_, i) => (
                <IconStarSolid16
                    key={`Quote-${quoteIndex}-Star-${i}`}
                    fill={primaryColor}
                    aria-hidden="true"
                />
            ))}
        </div>
    );
};

/**
 * Component for displaying press quotes/reviews in a grid layout.
 * This is specific to movie-landingpage and shows media reviews.
 * Full-width background with centered content.
 */
const PressQuotesSection: React.FC<{ quotes: PressQuote[] }> = ({ quotes }) => {
    const t = useTranslations();

    if (!quotes || quotes.length === 0) {
        return null;
    }

    return (
        <section
            className="py-48 text-center lg:text-left"
            id="reviews">
            <SectionHeader title={t('movie.press-quotes')} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-32 gap-x-36">
                {quotes.map((quote, index) => (
                    <div
                        key={`Quote-${index}`}
                        className="flex flex-col space-y-8">
                        <p className="text-body-14 italic">&ldquo;{quote.quote}&rdquo;</p>

                        {quote.rating && quote.rating > 0 ? (
                            <RatingStars
                                rating={quote.rating}
                                quoteIndex={index}
                            />
                        ) : null}

                        <div className="flex flex-col text-body-12 text-[var(--secondary-color)]">
                            <span className="font-semibold">
                                {quote.author}
                                {quote.source && (
                                    <Link
                                        href={quote.source}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={t('common.source-link', { author: quote.author })}>
                                        <IconExternalLink16
                                            className="w-12 h-12 inline-block ml-8"
                                            aria-hidden="true"
                                        />
                                    </Link>
                                )}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

export default PressQuotesSection;
